import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { ISession } from "../interfaces/Session";
import apiClient from "../api/apiClient";

interface IFetchSession {
    programId: string;
}

interface IStoreSessions {
    programId: string;
    sessions: ISession[];
}

interface IDeleteSession {
    sessionId: string;
    programId: string;
}

interface ProgramSessionsState {
    loading: boolean;
    error: string | null;
    programSessions: ISession[] | [];
}

const initialState: ProgramSessionsState = {
    loading: false,
    error: null,
    programSessions: []
};

/**
 * Axios request to fetch sessions for a given programId
 */
export const fetchSessions = createAsyncThunk('sessions/fetchSessions',
    async ({ programId }: IFetchSession, thunkAPI) => {
        try {
            const response = await apiClient.get(`/api/v1/programs/${programId}/sessions`);
            return response.data?.data?.sessions || [];
        } catch (error: any) {
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * AXIOS request for store sessions
 */
export const storeSessions = createAsyncThunk('sessions/storeSessions',
    async ({ programId, sessions }: IStoreSessions, thunkAPI) => {
        try {
            const response = await apiClient.post('/api/v1/sessions', {
                programId,
                sessions
            });
            thunkAPI.dispatch(fetchSessions({ programId }));
            return response.data?.data?.sessions || [];
        } catch (error: any) {
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * Axios API request to delete a session
 */
export const deleteSession = createAsyncThunk('sessions/deleteSession',
    async ({ sessionId, programId }: IDeleteSession, thunkAPI) => {
        try {
            const response = await apiClient.delete(`/api/v1/sessions/${sessionId}`);
            thunkAPI.dispatch(fetchSessions({ programId: programId?.toString() }));
            return response.data?.data?.sessions || [];
        } catch (error: any) {
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

const programSessionsSlice = createSlice({
    name: "programSessions",
    initialState,
    reducers: {
        resetState: (state) => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSessions.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchSessions.fulfilled, (state, action) => {
                state.loading = false;
                state.programSessions = action.payload;
            })
            .addCase(fetchSessions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'An error occurred';
            });
    },
});

export const { resetState } = programSessionsSlice.actions;
export default programSessionsSlice.reducer;